// src/ui/steps/step3-timesheet.jsx: Timesheet-invoer per categorie
// Volgens Handleiding stap 4: hoofdwerk, voorbereiding, afronding (+ pr/promotie voor sommige departementen).
import React from "react";
import {
  FW, FwButton,
  StepHeader, AsideCard,
} from "../primitives";
import { calc } from "../../lib/calc";
import { depToTimesheetKey } from "../../lib/dep-mapping";
import { timesheets } from "../../data/timesheets";

// Uren → werkdagen, afgerond op max 1 decimaal in nl-NL-notatie (komma).
const fmtDagen = (n) =>
  (Number.isFinite(n) ? n : 0).toLocaleString("nl-NL", { minimumFractionDigits: 0, maximumFractionDigits: 1 });

export const StepTimesheet = ({ state, setState, next, back, hideNext = false, niveauIsIndicatief = false, subOverride }) => {
  const huidigeRol = state.rollen?.[0];
  const isFestival = state.type === "festival";
  const timesheetKey = !isFestival && huidigeRol ? depToTimesheetKey(huidigeRol.dep) : null;
  const sjabloon = (timesheetKey && timesheets[timesheetKey]) || {};

  const tariefRij = huidigeRol
    ? (isFestival
      ? calc.tariefFestival(huidigeRol.niveau)
      : calc.tariefFilmAV(huidigeRol.niveau))
    : null;
  const uurtarief = tariefRij?.tarief_min || 0;

  const [rijen, setRijen] = React.useState(() => {
    const bestaand = huidigeRol?.urenDetails;
    if (bestaand && typeof bestaand === "object") {
      return {
        hoofdwerk:     bestaand.hoofdwerk     || [],
        voorbereiding: bestaand.voorbereiding || [],
        afronding:     bestaand.afronding     || [],
        pr_promotie:   bestaand.pr_promotie   || [],
      };
    }
    return { hoofdwerk: [], voorbereiding: [], afronding: [], pr_promotie: [] };
  });

  React.useEffect(() => {
    if (!huidigeRol) back();
  }, []);

  React.useEffect(() => {
    if (!huidigeRol) return;
    const sumCat = (cat) => (rijen[cat] || []).reduce((s, r) => s + (Number(r.uren) || 0), 0);
    setState(s => ({
      ...s,
      btw21: true,
      rollen: (s.rollen || []).map((r, i) => i === 0 ? {
        ...r,
        uren: {
          hoofdwerk:     sumCat("hoofdwerk"),
          voorbereiding: sumCat("voorbereiding"),
          afronding:     sumCat("afronding"),
          pr_promotie:   sumCat("pr_promotie"),
        },
        urenDetails: rijen,
      } : r),
    }));
  }, [rijen]);

  const categorieen = [
    { key: "voorbereiding", label: "Voorbereiding" },
    { key: "hoofdwerk",     label: "Hoofdwerkzaamheden" },
    { key: "afronding",     label: "Afronding" },
  ];
  if (sjabloon.pr_promotie?.length) {
    categorieen.push({ key: "pr_promotie", label: "PR & promotie" });
  }

  const urenPerCat = (cat) => rijen[cat].reduce((s, r) => s + (Number(r.uren) || 0), 0);
  const urenTotaal = categorieen.reduce((s, c) => s + urenPerCat(c.key), 0);

  if (!huidigeRol) return null;

  // Reset-knop: maakt alle ingevulde uren in één keer leeg.
  const resetTimesheet = () => {
    if (urenTotaal === 0) return;
    setRijen({ hoofdwerk: [], voorbereiding: [], afronding: [], pr_promotie: [] });
  };

  const commit = () => {
    const nieuweUren = {
      hoofdwerk:     urenPerCat("hoofdwerk"),
      voorbereiding: urenPerCat("voorbereiding"),
      afronding:     urenPerCat("afronding"),
      pr_promotie:   urenPerCat("pr_promotie"),
    };
    setState(s => ({
      ...s,
      btw21: true,
      rollen: (s.rollen || []).map((r, i) => i === 0 ? {
        ...r,
        uren: nieuweUren,
        urenDetails: rijen,
      } : r),
    }));
    next();
  };

  return (
    <div>
      <StepHeader
        tag="Film/AV-productie"
        perspectief={state.perspectief}
        title="Schat in hoeveel tijd de opdracht kost"
        sub={subOverride ?? (
          <>
            Beschrijf eerst je eigen werkzaamheden. Schat in hoeveel tijd je daarmee bezig bent. Hieronder vind je ook een overzicht van werkzaamheden die veel voorkomen in jouw departement. Vul alleen de effectieve werktijd in voor de opdracht zelf. Je eigen administratie, acquisitie en scholing zijn al verrekend in de zzp-opslagfactor. Meer informatie over werk- en rusttijden lees je in de{" "}
            <a
              href="https://www.filmwerkwijs.nl/afspraken-productie/wat-zijn-de-regels-voor-werken-op-een-productie"
              target="_blank"
              rel="noreferrer"
              style={{ color: FW.navy, fontWeight: 700, textDecoration: "underline" }}
            >
              Leidraad Veilig en Gezond Werken
            </a>
            , onder Rust- en werktijden &amp; planning.
          </>
        )}
        onBack={back}
      />

      {huidigeRol && (
        <div className="role-summary" style={{
          display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap",
          padding: "14px 18px", marginBottom: 20,
          background: FW.cream, border: `1.5px solid ${FW.gray100}`, borderRadius: 14,
        }}>
          <div style={{ fontSize: 14, color: FW.gray500, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Je rol</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: FW.navy }}>{huidigeRol.rol}</div>
          <div style={{ fontSize: 14, color: FW.navy2 }}>· {huidigeRol.dep}</div>
          {urenTotaal > 0 && (
            <button
              onClick={resetTimesheet}
              style={{
                marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 12px", borderRadius: 999,
                background: "transparent", color: FW.navy, border: `1.5px solid ${FW.taupe}`,
                fontSize: 13, fontWeight: 700, fontFamily: "var(--font-sans)", cursor: "pointer",
                transition: "background .15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = FW.beige2; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
            >
              <i className="ri-eraser-line" style={{ fontSize: 15 }} /> Timesheet leegmaken
            </button>
          )}
        </div>
      )}

      <div className="row-2col" style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 24, alignItems: "start" }}>
        <div style={{ display: "grid", gap: 20 }}>
          {categorieen.map(c => (
            <TimesheetSectie
              key={c.key}
              label={c.label}
              sjabloonActiviteiten={sjabloon[c.key] || []}
              rijen={rijen[c.key]}
              setRijen={(nieuw) => setRijen(r => ({ ...r, [c.key]: nieuw }))}
              catKey={c.key}
              uurtarief={uurtarief}
            />
          ))}

        </div>

        <AsideCard>
          <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 10, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <i className="ri-calculator-line" /> Lopend totaal
          </div>
          <div style={{ display: "grid", gap: 6, fontSize: 14, color: FW.navy2 }}>
            {categorieen.map(c => (
              <div key={c.key} style={{ display: "flex", justifyContent: "space-between" }}>
                <span>{c.label}</span>
                <span style={{ fontWeight: 700, color: FW.navy }}>{urenPerCat(c.key)} uur</span>
              </div>
            ))}
          </div>
          <div style={{ height: 1, background: FW.gray100, margin: "12px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15 }}>
            <span style={{ color: FW.navy2 }}>Totaal uren</span>
            <span style={{ fontWeight: 700, color: FW.navy }}>{urenTotaal} uur</span>
          </div>
          {/* Niemand in de sector rekent in uren: iedereen in werkdagen.
              Toon de omrekening naar werkdagen van 8,5 en 9,5 uur. */}
          <div style={{ marginTop: 10, display: "grid", gap: 4, fontSize: 13, color: FW.navy2 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span>Werkdagen van 8,5 uur</span>
              <span style={{ fontWeight: 700, color: FW.navy }}>{fmtDagen(urenTotaal / 8.5)} dagen</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span>Werkdagen van 9,5 uur</span>
              <span style={{ fontWeight: 700, color: FW.navy }}>{fmtDagen(urenTotaal / 9.5)} dagen</span>
            </div>
          </div>
          {niveauIsIndicatief && huidigeRol?.niveau && (
            <div style={{ marginTop: 14, padding: "10px 12px", background: FW.cream, borderRadius: 10, fontSize: 12, color: FW.navy2, lineHeight: 1.45 }}>
              <i className="ri-information-line" style={{ marginRight: 6 }} />
              We tonen indicatieve werkzaamheden o.b.v. zwaarteniveau <b>{huidigeRol.niveau}</b>. Voor een onderbouwd openingsbod doe je de volledige Fair Pay calculatie.
            </div>
          )}
        </AsideCard>
      </div>

      <div className="step-nav" style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }}>
        {back
          ? <FwButton variant="ghost" arrow={false} onClick={back}>← Terug</FwButton>
          : <div />}
        {!hideNext && (
          <FwButton variant="primary" onClick={commit} disabled={urenTotaal === 0}>
            Verder naar Zwaarteniveau
          </FwButton>
        )}
      </div>
    </div>
  );
};

const TimesheetSectie = ({ label, sjabloonActiviteiten, rijen, setRijen, catKey, uurtarief }) => {
  const [toonAlles, setToonAlles] = React.useState(false);
  const DEFAULT_TONEN = 5;

  const zichtbareActiviteiten = toonAlles
    ? sjabloonActiviteiten
    : sjabloonActiviteiten.slice(0, DEFAULT_TONEN);

  const setUrenVoorAct = (act, raw) => {
    const val = Math.min(500, Math.max(0, Number(raw) || 0));
    const bestaande = rijen.find(r => r.activiteit === act && !r.custom);
    if (val === 0 && bestaande) {
      setRijen(rijen.filter(r => r.id !== bestaande.id));
    } else if (val > 0 && !bestaande) {
      setRijen([...rijen, {
        id: window.crypto?.randomUUID?.() || `${catKey}-${Date.now()}-${Math.random()}`,
        activiteit: act,
        uren: val,
      }]);
    } else if (bestaande) {
      setRijen(rijen.map(r => r.id === bestaande.id ? { ...r, uren: val } : r));
    }
  };

  const updateUren = (id, uren) => {
    const val = Math.min(500, Math.max(0, Number(uren) || 0));
    setRijen(rijen.map(r => r.id === id ? { ...r, uren: val } : r));
  };

  const updateCustomLabel = (id, label) => {
    setRijen(rijen.map(r => r.id === id ? { ...r, activiteit: label } : r));
  };

  const verwijder = (id) => {
    setRijen(rijen.filter(r => r.id !== id));
  };

  const voegEigenToe = () => {
    setRijen([...rijen, {
      id: window.crypto?.randomUUID?.() || `${catKey}-custom-${Date.now()}`,
      activiteit: "",
      uren: 0,
      custom: true,
    }]);
  };

  const totaalUren = rijen.reduce((s, r) => s + (Number(r.uren) || 0), 0);
  const customRijen = rijen.filter(r => r.custom);

  return (
    <div style={{
      padding: 20, background: FW.white,
      border: `1.5px solid ${FW.gray100}`, borderRadius: 18,
    }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 12, paddingBottom: 14, marginBottom: 16,
        borderBottom: `1px solid ${FW.gray100}`,
      }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: FW.navy, letterSpacing: "-0.005em" }}>{label}</div>
        <div style={{
          fontSize: 13, fontWeight: 700,
          padding: "4px 12px", borderRadius: 999,
          background: totaalUren > 0 ? FW.beige2 : FW.gray50,
          color: totaalUren > 0 ? FW.navy : FW.gray500,
          flexShrink: 0,
        }}>{totaalUren} uur</div>
      </div>
      {/* Eigen werkzaamheden eerst: dit is wat de gebruiker zelf inschat.
          De voorgevulde lijst eronder dient slechts als geheugensteun. */}
      {customRijen.length > 0 && (
        <div style={{ display: "grid", gap: 6 }}>
          {customRijen.map(rij => (
            <div key={rij.id} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 10px", background: FW.cream, borderRadius: 10, border: `1px solid ${FW.gray100}`,
            }}>
              <i className="ri-edit-line" style={{ fontSize: 14, color: FW.gray500 }} />
              <input type="text" placeholder="Eigen werkzaamheden…" value={rij.activiteit}
                onChange={e => updateCustomLabel(rij.id, e.target.value)}
                style={{
                  flex: 1, border: 0, background: "transparent", fontSize: 14, color: FW.navy,
                  fontFamily: "var(--font-sans)", outline: "none", padding: "4px 0",
                }} />
              <button
                onClick={() => verwijder(rij.id)}
                aria-label="Verwijder werkzaamheden"
                title="Verwijder"
                onMouseEnter={e => { e.currentTarget.style.color = "#B0384B"; }}
                onMouseLeave={e => { e.currentTarget.style.color = FW.gray500; }}
                style={{
                  background: "transparent", border: 0, cursor: "pointer",
                  color: FW.gray500, padding: 4,
                  display: "grid", placeItems: "center",
                  transition: "color .15s",
                }}
              >
                <i className="ri-delete-bin-line" style={{ fontSize: 16 }} />
              </button>
              <UrenStepper
                value={rij.uren}
                onChange={(v) => updateUren(rij.id, v)}
              />
              <span style={{ fontSize: 13, color: FW.gray500 }}>uur</span>
            </div>
          ))}
        </div>
      )}

      <button onClick={voegEigenToe} style={{
        marginTop: customRijen.length > 0 ? 8 : 0,
        background: "transparent", border: `1.5px dashed ${FW.gray100}`, color: FW.navy,
        borderRadius: 10, padding: "8px 14px", fontSize: 13, fontWeight: 700,
        cursor: "pointer", fontFamily: "var(--font-sans)",
      }}>
        + Voeg eigen werkzaamheden toe
      </button>

      {sjabloonActiviteiten.length > 0 && (
        <div style={{ marginTop: 20, marginBottom: 12, borderTop: `1px solid ${FW.gray100}` }} />
      )}

      <div style={{ display: "grid", gap: 6 }}>
        {zichtbareActiviteiten.map((act, i) => {
          const rij = rijen.find(r => r.activiteit === act && !r.custom);
          const actief = !!rij && rij.uren > 0;
          return (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 10px",
              background: actief ? FW.cream : "transparent",
              borderRadius: 10,
              border: actief ? `1px solid ${FW.gray100}` : "1px solid transparent",
            }}>
              <div style={{ flex: 1, fontSize: 14, color: FW.navy, lineHeight: 1.4 }}>{act}</div>
              {/* Spacer reserveert dezelfde ruimte als de prullenbak in custom-rijen,
                  zodat de stepper + 'uur' op exact dezelfde X-positie staan. */}
              <div style={{ width: 24, height: 24, flexShrink: 0 }} aria-hidden="true" />
              <UrenStepper
                value={rij?.uren || 0}
                onChange={(v) => setUrenVoorAct(act, v)}
              />
              <span style={{ fontSize: 13, color: FW.gray500 }}>uur</span>
            </div>
          );
        })}
      </div>

      {sjabloonActiviteiten.length > DEFAULT_TONEN && (
        <div style={{ marginTop: 14 }}>
          <button onClick={() => setToonAlles(!toonAlles)} style={{
            background: "transparent", border: 0, color: FW.navy,
            fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-sans)",
            textDecoration: "underline", textUnderlineOffset: 3, padding: 0,
          }}>
            {toonAlles ? "Toon minder" : `Toon nog ${sjabloonActiviteiten.length - DEFAULT_TONEN} werkzaamheden`}
          </button>
        </div>
      )}
    </div>
  );
};

// Uren-invoer met subtiele −/+ stappers links en rechts van het cijferveld.
// Knoppen en input zitten samen in één container met gedeelde border zodat
// het oogt als één stepper-element. Het veld zelf blijft typbaar.
const UrenStepper = ({ value, onChange }) => {
  const num = Number(value) || 0;
  const aanpassen = (delta) => onChange(Math.max(0, num + delta));
  return (
    <div style={{
      display: "inline-flex", alignItems: "stretch",
      border: `1.5px solid ${FW.gray100}`, borderRadius: 8,
      background: FW.white, overflow: "hidden",
    }}>
      <StepperBtn icon="ri-subtract-line" aria="Verlaag uren"
        onClick={() => aanpassen(-1)} disabled={num === 0} />
      <input
        type="number" min="0"
        value={num || ""} placeholder="0"
        onChange={e => onChange(e.target.value)}
        style={{
          width: 32, border: 0, background: "transparent",
          padding: "5px 0", fontSize: 14, fontWeight: 700, color: FW.navy,
          fontFamily: "var(--font-sans)", textAlign: "center", outline: "none",
        }}
      />
      <StepperBtn icon="ri-add-line" aria="Verhoog uren"
        onClick={() => aanpassen(1)} />
    </div>
  );
};

const StepperBtn = ({ icon, aria, onClick, disabled }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    aria-label={aria}
    disabled={disabled}
    onMouseEnter={e => !disabled && (e.currentTarget.style.background = FW.cream)}
    onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
    style={{
      width: 22, padding: 0,
      background: "transparent", border: 0,
      color: disabled ? FW.gray500 : FW.navy,
      cursor: disabled ? "default" : "pointer",
      display: "grid", placeItems: "center",
      opacity: disabled ? 0.35 : 1,
      transition: "background .15s",
    }}
  >
    <i className={icon} style={{ fontSize: 13 }} />
  </button>
);

